import unlockCodeService from './unlockCodeService';
import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess';

/** @param {unknown} lockoutData */
function parseLockoutStoredValue(lockoutData) {
  if (!lockoutData) return null;
  if (typeof lockoutData === 'object' && lockoutData !== null) return lockoutData;
  if (typeof lockoutData !== 'string') return null;
  if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') return null;
  try {
    return JSON.parse(lockoutData);
  } catch {
    return null;
  }
}

class LockoutService {
  constructor() {
    this.LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
    this.MAX_ATTEMPTS = 3;
    this.STORAGE_KEY = 'codeEntryLockout';
  }

  // Check if the system is currently locked out
  isLockedOut() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) {
        // Only log once per session to reduce console spam
        if (!this._hasLoggedNoData) {

          this._hasLoggedNoData = true;
        }
        return false;
      }

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      
      // Only lock out if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return false;
      }
      
      const now = Date.now();

      // If lockout period has expired, reset
      if (now >= lockoutUntil) {

        this.resetLockout();
        return false;
      }

      // Only log lockout status once per minute to reduce console spam
      const nowTime = Date.now();
      if (!this._lastLockoutLog || (nowTime - this._lastLockoutLog) > 60000) {

        this._lastLockoutLog = nowTime;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking lockout status:', error);
      return false;
    }
  }

  // Check if lockout is about to expire (within 1 minute)
  isLockoutExpiringSoon() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      const parsed = parseLockoutStoredValue(lockoutData);
      if (!parsed) return false;

      const { lockoutUntil } = parsed;
      const now = Date.now();
      const timeUntilExpiry = lockoutUntil - now;
      
      // Return true if lockout expires within 1 minute
      return timeUntilExpiry > 0 && timeUntilExpiry <= 60000;
    } catch (error) {
      console.error('Error checking if lockout is expiring soon:', error);
      return false;
    }
  }

  // Get remaining lockout time in minutes and seconds
  getRemainingLockoutTime() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return { minutes: 0, seconds: 0 };

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      
      // Only show remaining time if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return { minutes: 0, seconds: 0 };
      }
      
      const now = Date.now();
      const remaining = Math.max(0, lockoutUntil - now);

      if (remaining <= 0) {
        this.resetLockout();
        return { minutes: 0, seconds: 0 };
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      return { minutes, seconds };
    } catch (error) {
      console.error('Error getting remaining lockout time:', error);
      return { minutes: 0, seconds: 0 };
    }
  }

  // Get detailed lockout information
  getDetailedLockoutInfo() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      const parsed = parseLockoutStoredValue(lockoutData);
      if (!parsed) return null;

      const { lockoutUntil, attempts, lastAttempt } = parsed;
      
      // Only return detailed info if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return null;
      }
      
      const now = Date.now();
      const remaining = Math.max(0, lockoutUntil - now);
      const isExpired = now >= lockoutUntil;
      const isExpiringSoon = remaining > 0 && remaining <= 60000;

      if (isExpired) {
        this.resetLockout();
        return null;
      }

      return {
        lockoutUntil,
        attempts,
        lastAttempt,
        remaining,
        isExpired: false,
        isExpiringSoon,
        minutes: Math.floor(remaining / 60000),
        seconds: Math.floor((remaining % 60000) / 1000)
      };
    } catch (error) {
      console.error('Error getting detailed lockout info:', error);
      return null;
    }
  }

  // Record a failed attempt
  recordFailedAttempt() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      let attempts = 1;
      let lockoutUntil = null;

      if (lockoutData) {
        const existing = parseLockoutStoredValue(lockoutData);
        if (existing) {
          attempts = existing.attempts + 1;
        }

        
        // If this is the 3rd failed attempt, set lockout
        if (attempts >= this.MAX_ATTEMPTS) {
          lockoutUntil = Date.now() + this.LOCKOUT_DURATION;

        } else {

        }
      } else {

      }

      const newLockoutData = {
        attempts,
        lockoutUntil,
        lastAttempt: Date.now()
      };

      kvSet(this.STORAGE_KEY, newLockoutData);

      // If lockout is triggered, play alarm sound
      if (lockoutUntil) {
        this.triggerAlarm();
      }

      return {
        attempts,
        isLockedOut: !!lockoutUntil,
        lockoutUntil
      };
    } catch (error) {
      console.error('Error recording failed attempt:', error);
      return { attempts: 1, isLockedOut: false, lockoutUntil: null };
    }
  }

  // Reset lockout (called when successful login or lockout expires)
  resetLockout() {
    try {
      kvRemove(this.STORAGE_KEY);

      
      // Reset logging flags
      this._hasLoggedNoData = false;
      this._lastLockoutLog = null;
    } catch (error) {
      console.error('Error resetting lockout:', error);
    }
  }

  // Reset lockout only on successful code entry (not on logout)
  resetLockoutOnSuccess() {
    try {
      kvRemove(this.STORAGE_KEY);

    } catch (error) {
      console.error('Error resetting lockout on success:', error);
    }
  }

  // Check if lockout should persist across logout
  shouldPersistLockout() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return false;

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      
      // Only persist if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return false;
      }
      
      const now = Date.now();
      
      // If lockout period has expired, don't persist
      if (now >= lockoutUntil) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking if lockout should persist:', error);
      return false;
    }
  }

  // Get current attempt count
  getAttemptCount() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return 0;

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in getAttemptCount, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return 0;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in getAttemptCount:', parseError);
          kvRemove(this.STORAGE_KEY);
          return 0;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return 0;
      }
      
      const { attempts } = parsedData;
      return attempts || 0;
    } catch (error) {
      console.error('Error getting attempt count:', error);
      return 0;
    }
  }

  // Trigger alarm sound and visual notification
  triggerAlarm() {
    try {
      // Play alarm sound if available
      this.playAlarmSound();
      
      // Show visual notification
      this.showLockoutNotification();
      

    } catch (error) {
      console.error('Error triggering alarm:', error);
    }
  }

  // Play alarm sound
  playAlarmSound() {
    try {
      // Create audio context for alarm sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Set alarm sound properties
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      // Repeat alarm 3 times
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator2.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
        oscillator2.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
        oscillator2.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);
        
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.5);
      }, 600);

      setTimeout(() => {
        const oscillator3 = audioContext.createOscillator();
        const gainNode3 = audioContext.createGain();
        
        oscillator3.connect(gainNode3);
        gainNode3.connect(audioContext.destination);
        
        oscillator3.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator3.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
        oscillator3.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
        oscillator3.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);
        
        gainNode3.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator3.start(audioContext.currentTime);
        oscillator3.stop(audioContext.currentTime + 0.5);
      }, 1200);

    } catch (error) {
      console.error('Error playing alarm sound:', error);
    }
  }

  // Show lockout notification
  showLockoutNotification() {
    try {
      // Check if document, head, and body exist
      if (!document || !document.head || !document.body) {
        console.warn('Document, head, or body not available for lockout notification');
        return;
      }

      // Create a prominent notification element
      const notification = document.createElement('div');
      notification.className = 'ads-lockout-toast';
      notification.setAttribute('role', 'alert');

      notification.innerHTML = `
        <div class="ads-lockout-toast__title">SYSTEM LOCKED OUT</div>
        <div class="ads-lockout-toast__body">
          Too many failed attempts!<br>
          Access blocked for 15 minutes
        </div>
      `;

      document.body.appendChild(notification);

      // Remove notification after 10 seconds
      setTimeout(() => {
        try {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        } catch (error) {
          console.error('Error removing lockout notification:', error);
        }
      }, 10000);

    } catch (error) {
      console.error('Error showing lockout notification:', error);
    }
  }

  // Get current lockout status for display
  getLockoutStatus() {
    const isLocked = this.isLockedOut();
    const attempts = this.getAttemptCount();
    const remaining = this.getRemainingLockoutTime();

    return {
      isLocked,
      attempts,
      remaining,
      maxAttempts: this.MAX_ATTEMPTS
    };
  }

  // Get lockout status that persists across logout
  getPersistentLockoutStatus() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return null;

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      const now = Date.now();
      
      // Only return status if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return null;
      }
      
      const remaining = Math.max(0, lockoutUntil - now);
      
      if (remaining <= 0) {
        this.resetLockout();
        return null;
      }

      return {
        isLocked: true,
        attempts,
        remaining: {
          minutes: Math.floor(remaining / 60000),
          seconds: Math.floor((remaining % 60000) / 1000)
        },
        maxAttempts: this.MAX_ATTEMPTS,
        lockoutUntil
      };
    } catch (error) {
      console.error('Error getting persistent lockout status:', error);
      return null;
    }
  }

  // Check if user should see lockout warning when returning to app
  shouldShowLockoutWarning() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return false;

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      
      // Only show warning if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return false;
      }
      
      const now = Date.now();
      
      // If lockout period has expired, don't show warning
      if (now >= lockoutUntil) {
        this.resetLockout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking if should show lockout warning:', error);
      return false;
    }
  }

  // Get lockout warning message for returning users
  getLockoutWarningMessage() {
    try {
      const lockoutData = kvGet(this.STORAGE_KEY);
      if (!lockoutData) return null;

      // getItem already parses JSON, but handle both cases
      let parsedData;
      if (typeof lockoutData === 'string') {
        if (lockoutData === '[object Object]' || lockoutData === '"[object Object]"' || lockoutData.trim() === '') {
          console.warn('⚠️ [LOCKOUT] Corrupted lockout data in isLockedOut, cleaning up');
          kvRemove(this.STORAGE_KEY);
          return false;
        }
        try {
          parsedData = JSON.parse(lockoutData);
        } catch (parseError) {
          console.error('Error parsing lockout data in isLockedOut:', parseError);
          kvRemove(this.STORAGE_KEY);
          return false;
        }
      } else if (typeof lockoutData === 'object' && lockoutData !== null) {
        parsedData = lockoutData;
      } else {
        return false;
      }
      
      const { lockoutUntil, attempts } = parsedData;
      const now = Date.now();
      
      // Only return message if we have 3 or more attempts AND a lockout time
      if (attempts < this.MAX_ATTEMPTS || !lockoutUntil) {
        return null;
      }
      
      const remaining = Math.max(0, lockoutUntil - now);
      
      if (remaining <= 0) {
        this.resetLockout();
        return null;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      return {
        message: `System is still locked out due to ${attempts} failed code attempts. Please wait ${minutes}:${seconds.toString().padStart(2, '0')} before trying again.`,
        remaining: { minutes, seconds },
        attempts
      };
    } catch (error) {
      console.error('Error getting lockout warning message:', error);
      return null;
    }
  }

  // Validate unlock code and override lockout
  async validateUnlockCode(businessId, unlockCode) {
    try {
      const result = await unlockCodeService.validateUnlockCode(businessId, unlockCode);
      
      if (result.success && result.valid) {
        // Unlock code is valid - reset the lockout
        this.resetLockout();

        return { success: true, message: 'Lockout overridden successfully' };
      } else {
        return { success: false, message: result.message || 'Invalid unlock code' };
      }
    } catch (error) {
      console.error('Error validating unlock code:', error);
      return { success: false, message: 'Error validating unlock code' };
    }
  }

  // Check if unlock code is available for a business
  async isUnlockCodeAvailable(businessId) {
    try {
      return await unlockCodeService.hasUnlockCode(businessId);
    } catch (error) {
      console.error('Error checking unlock code availability:', error);
      return false;
    }
  }
  
  // Reset logging state (useful for debugging)
  resetLoggingState() {
    this._hasLoggedNoData = false;
    this._lastLockoutLog = null;

  }
}

// Create singleton instance
const lockoutService = new LockoutService();

export default lockoutService;
